import { Feature } from '~/graphql/generated/query.types'

export type FeatureFlagConfig = {
  label: string
  description?: string
  isHidden?: boolean
  forceEnabledForDemoFirms: boolean
  forDemosOnly?: boolean
}

export const featureFlagConfigs: Record<Feature, FeatureFlagConfig> = {
  // Visible flags (flags are still in use)
  [Feature.overviewDashboard]: { label: 'Overview dashboard', forceEnabledForDemoFirms: true },
  [Feature.renewalForecasting]: {
    label: 'Renewal forecasting',
    description:
      'Use renewal predictions model in availability forecast and lease expiration forecasting. If disabled, lease expiration forecasting will use the renewal rate assumption setting.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.reports]: {
    label: 'Reports',
    description:
      'Adds some example custom reports, and shows the scheduled reports tab. Only for demo purposes.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  // TODO(scott, 2025-09-11): Clean this flag up since we have the firm column
  // shouldGeneratePriceRecommendations
  [Feature.priceRecommendations]: {
    label: 'Price recommendations',
    forceEnabledForDemoFirms: false,
  },
  [Feature.priceRecommendationScenarioView]: {
    label: 'Price recommendation scenario view',
    description:
      'For internal testing of price recommendation scenarios. Do not turn on for clients.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.priceRecommendationFeedback]: {
    label: 'Price recommendation feedback',
    description:
      'For customers to provide feedback on price recommendations. Not really used anymore.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.renewalsScheduledUpdates]: {
    label: 'Renewals scheduled updates',
    description:
      'For demo and internal testing of renewals scheduled updates. Feature is not complete.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  [Feature.autoPilot]: { label: 'AutoPilot', isHidden: false, forceEnabledForDemoFirms: true },
  [Feature.propertySettingsBulkImport]: {
    label: 'Property settings bulk import',
    forceEnabledForDemoFirms: true,
  },
  [Feature.automaticLeaseExpirationTargets]: {
    label: 'Automatic lease expiration targets',
    description: 'For demo of automatic lease expiration targets. Feature is not complete.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  [Feature.monthToMonthExpirationForecasting]: {
    label: 'Month-to-month expiration forecasting',
    description:
      'Use month-to-month expiration forecasting in availability forecast and recommendations.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.renewalShortTermPenaltyFactor]: {
    label: 'Renewal short-term penalty factor',
    description: 'Gives option to turn on renewal short-term penalty.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.gradualLeaseExpirationPenaltyControls]: {
    label: 'Gradual lease expiration penalty controls',
    description:
      'Gives option to turn off gradual lease expiration penalty for new leases and renewals.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.upcomingAppointmentsView]: {
    label: 'Upcoming appointments',
    description:
      'Shows the upcoming appointments stat in property stats bar. For Entrata firms only.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.shouldOmitPricesForNewLeaseTermsOverExpirationCap]: {
    label: 'Omit new lease terms over expiration caps (Montgomery County)',
    description:
      'Omit new lease terms over expiration caps in rent matrix calculations, which was requested by certain clients in Montgomery County.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.seasonalBiasCorrection]: {
    label: 'Seasonal bias correction',
    description: 'Gives option to turn on seasonal bias correction rent matrix settings.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.montgomeryCountyRentControl]: {
    label: 'Montgomery County rent control',
    forceEnabledForDemoFirms: true,
  },
  [Feature.earlyLeaseTerminationForecasting]: {
    label: 'Early lease termination forecasts',
    description:
      'Use early lease termination forecasts in availability forecast and recommendations.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.customUnitGroupPriceRecommendations]: {
    label: 'Custom unit group price recommendations',
    description:
      'Gives option to turn on custom unit group price recommendations (instead of bedroom count).',
    forceEnabledForDemoFirms: false,
  },
  [Feature.maxBestTermSetting]: {
    label: 'Maximum best term setting',
    description: 'Gives option to set a maximum best term in rent matrix settings.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.distributeGuestcardsByAvailability]: {
    label: 'Distribute guestcards by availability',
    description:
      'Changes guestcard distribution to be based on unit availability, when bed count (or unit type) is not specified.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.overviewPortfolioRollUps]: {
    label: 'Overview portfolio roll-ups',
    description: 'Show portfolio roll-ups on the overview page.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.hideCompPropertiesView]: {
    label: 'Hide comp properties view',
    forceEnabledForDemoFirms: false,
  },
  // TODO(scott, 2025-09-12): Remove this flag
  [Feature.pricingAnalysis]: {
    label: 'Pricing analysis',
    description: 'Show generated pricing analyses when price recommendations are disabled.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.alertsAndInsights]: {
    label: 'Alerts and insights',
    forceEnabledForDemoFirms: false,
  },
  [Feature.alertsAndInsightsForPortfolioAndFirm]: {
    label: 'Alerts and insights (portfolio and firm)',
    forceEnabledForDemoFirms: false,
  },
  [Feature.editUnitAmenities]: {
    label: 'Edit unit amenities',
    description: 'Gives option to edit unit amenities for demo only.',
    forceEnabledForDemoFirms: false,
    forDemosOnly: true,
  },
  [Feature.propertySummaryEmail]: {
    label: 'Property summary email',
    description: 'Show settings for turning on property summary emails.',
    forceEnabledForDemoFirms: true,
  },
  [Feature.unitRentControlOverrides]: {
    label: 'Unit rent control overrides',
    description:
      'Gives option to set a rent control override for a unit. Rent matrix prices cannot exceed this limit for both new leases and renewals.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.netApplicationsMetric]: {
    label: 'Net applications metric',
    description:
      'Use new net applications metric, calculated as applications - cancellations - denials.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.rentanaChat]: {
    label: 'Rentana chat',
    description: 'Enable the new Rentana chat experience.',
    forceEnabledForDemoFirms: false,
  },

  // Hidden flags (isHidden: true) that are no longer referenced in the codebase
  [Feature.unitGroupPropertySettingOverrides]: {
    label: 'Unit group property setting overrides',
    isHidden: true,
    forceEnabledForDemoFirms: false,
  },
  [Feature.budgetedRentTracking]: {
    label: 'Budgeted rent tracking',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.pricingReviewV2]: {
    label: 'Pricing review v2',
    isHidden: true,
    forceEnabledForDemoFirms: false,
  },
  [Feature.priceRecommendationDemo]: {
    label: 'Price recommendation demo',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.renewals]: { label: 'Renewals', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.metricsPage]: { label: 'Metrics page', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.propertyPricingSummaryEmail]: {
    label: 'Property pricing summary email',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.newEmail]: { label: 'New email', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.lossToLease]: { label: 'Loss to lease', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.customUnitGroupings]: {
    label: 'Custom unit groupings',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.specials]: { label: 'Specials', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.amenities]: { label: 'Amenities', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.renewalsSummaryView]: {
    label: 'Renewals summary view',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.priceRecommendationsPage]: {
    label: 'Price history page',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.createMultipleRenewalsBatches]: {
    label: 'Create multiple renewals batches',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.alertsPage]: { label: 'Alerts page', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.priceHistoryV2]: {
    label: 'Price history v2',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.renewalsMonthToMonthView]: {
    label: 'Renewals month-to-month view',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.priceRecommendationsUnitAvailability]: {
    label: 'Unit availability for recommendations',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.occupancyTargetOverrides]: {
    label: 'Occupancy target overrides',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.leaseTradeOutReports]: {
    label: 'Lease trade-out reports',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.navRedesign]: { label: 'Nav redesign', isHidden: true, forceEnabledForDemoFirms: true },
  [Feature.marketRentAdjustment]: {
    label: 'Market rent adjustment',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.recipientGroups]: {
    label: 'Recipient groups',
    isHidden: true,
    forceEnabledForDemoFirms: true,
  },
  [Feature.priceRecommendationsCompLowerLimit]: {
    label: 'Comps-based lower limit for recommendations',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.exposeCompressedPriceRecommendation]: {
    label: 'Expose pre-limits price recommendation in user-facing explanation',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.recentAchievedAverageMarketRent]: {
    label: 'Recent achieved average market rent',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.executedLeasesReport]: {
    label: 'Executed leases report',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.offerAnalysis]: {
    label: 'Offer analysis',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.leaseToMoveInConversion]: {
    label: 'Use lease to move-in conversion in models',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.compsV2]: {
    label: 'Comps v2',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.leaseComplianceReport]: {
    label: 'Lease compliance report',
    forceEnabledForDemoFirms: true,
    isHidden: true,
  },
  [Feature.compsInsights]: {
    label: 'Comps insights',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.leaseTradeOutReport]: {
    label: 'Lease trade-out report',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.propertyLeads]: {
    label: 'Property leads page',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.automaticRenewalRequests]: {
    label: 'Automatic renewal batches',
    forceEnabledForDemoFirms: false,
    isHidden: true,
  },
  [Feature.frigadeAssistant]: {
    label: 'Frigade assistant',
    forceEnabledForDemoFirms: false,
  },
  [Feature.prospectiveProperties]: {
    label: 'Prospective properties',
    forceEnabledForDemoFirms: false,
  },
  [Feature.leadSourceTracking]: {
    label: 'Lead source tracking',
    forceEnabledForDemoFirms: true,
  },
  [Feature.sso]: {
    label: 'Okta SSO',
    forceEnabledForDemoFirms: false,
  },
  [Feature.combinedProperties]: {
    label: 'Combined properties',
    description: 'Enables the combined properties feature.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.marketRentAdjustmentsInPricingReview]: {
    label: 'Market rent adjustments in pricing review',
    description:
      'Adds market rent adjustments and market rent adjustment suggestions to the pricing review modal.',
    forceEnabledForDemoFirms: false,
  },
  [Feature.leaseUps]: {
    label: 'Lease up',
    description:
      'Enables lease-up mode for properties, including the property state selector and lease-up plans.',
    forceEnabledForDemoFirms: false,
  },
}
